import { useEffect, useMemo, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { tagsApi } from '../../lib/api';
import { useTagStore } from '../../store/tagStore';
import type { Tag } from '../../types/tag';

interface TagPickerProps { kind: string; entityId: string; }

// Mirror the backend's name→code slugify (lowercase, non-alnum → '-', trim dashes)
// so the Create-row gate compares like-for-like against an existing tag's `code`.
const toSlug = (s: string) => s.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

export const TagPicker = ({ kind, entityId }: TagPickerProps) => {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [vocabulary, setVocabulary] = useState<Tag[]>([]);
  const { byRecord, apply } = useTagStore();
  const applied = byRecord[`${kind}:${entityId}`] ?? [];
  const appliedIds = new Set(applied.map((t) => t.id));

  const dropdownRef = useRef<HTMLDivElement>(null);

  // The visible-tag vocabulary (shared + own private) is small, so load it once and
  // filter client-side for instant type-ahead. Refresh on focus so a tag created
  // elsewhere shows up without a reload.
  const loadVocabulary = () => {
    tagsApi
      .list()
      .then((tags: Tag[]) => setVocabulary(tags))
      .catch(() => { /* keep the prior vocabulary on a transient failure */ });
  };
  useEffect(() => { loadVocabulary(); }, []);

  // Instant in-memory match on code OR name — no per-keystroke fetch, no debounce lag.
  const options = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    return vocabulary.filter(
      (t) => t.code.toLowerCase().includes(needle) || t.name.toLowerCase().includes(needle),
    );
  }, [q, vocabulary]);

  // Close the menu when clicking outside (house idiom from OrganizationTypeahead).
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const applyExisting = async (tag: Tag) => {
    try {
      await apply(kind, entityId, tag);
      setQ('');
      setOpen(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not apply tag');
    }
  };

  const createAndApply = async () => {
    let created: Tag;
    try {
      created = await tagsApi.create({ name: q.trim() }); // private + auto-slug
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not create tag');
      return;
    }
    // Fold the new tag into the local vocabulary so it's matchable immediately.
    setVocabulary((prev) => (prev.some((t) => t.id === created.id) ? prev : [...prev, created]));
    try {
      await apply(kind, entityId, created);
    } catch (err: any) {
      // tagStore.apply rolls back the optimistic add but doesn't toast — surface
      // an apply-specific message so we don't mislead with "Could not create tag".
      toast.error(err?.response?.data?.message || 'Could not apply tag');
    }
    setQ('');
    setOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <input
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          setOpen(true);
          loadVocabulary();
        }}
        aria-label="Add a tag"
        placeholder="Add a tag…"
        className="input text-sm py-1.5"
      />
      {open && q.trim() && (
        <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {options.map((t) => {
            const isApplied = appliedIds.has(t.id);
            return (
              <button
                key={t.id}
                type="button"
                disabled={isApplied}
                onClick={() => applyExisting(t)}
                className={`block w-full text-left px-3 py-1.5 text-sm ${
                  isApplied
                    ? 'text-gray-400 dark:text-gray-500 cursor-default'
                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {t.code}{t.isShared ? '' : ' · private'}{isApplied ? ' · added' : ''}
              </button>
            );
          })}
          {toSlug(q) && !options.some((t) => t.code === toSlug(q)) && (
            <button
              type="button"
              onClick={createAndApply}
              className="flex items-center gap-1 w-full text-left px-3 py-1.5 text-sm text-blast-600 dark:text-blast-400 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <Plus className="h-3 w-3" /> Create “{q.trim()}”
            </button>
          )}
        </div>
      )}
    </div>
  );
};
